/* eslint-disable no-param-reassign */
/**
 * CampaignPerformance.slice.js
 *
 * BRONZE layer (medallion architecture) for the RADzilla Performance Dashboard
 * (AAPU-6814/AAPU-6815, epic AAPU-6270). State here mirrors the backend response
 * shapes as-is — no normalization, no display formatting. See
 * `CampaignPerformance.selectors.js` (SILVER) and `hooks/usePerformanceData.js`
 * (GOLD) for the layers that derive from this one.
 *
 * Registered at `state.CampaignPerformance` in src/store.js — kept as a feature-root
 * slice (not nested under a `slices/` folder), matching the one proven, actually-wired
 * sibling-slice pattern in this domain (`Outreach.slice.js`). The `Campaigns/slices/`
 * modular split (CampaignCore, CampaignReporting, etc.) is dead code — never
 * registered in the store — and is not a pattern to follow.
 */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../../../util/api_client';
import { isApiSuccess } from '../../../../constants/platformStatusCodes';
import {
  ALL_NETWORKS_KEY,
  DEFAULT_NETWORK,
  DEFAULT_TIMELINE_RANGE,
  GENERIC_ERROR_MESSAGE,
  LIST_PER_PAGE,
} from './constants';

// One block per fetch, all sharing the same `{ data, meta, loading, error }` shape and
// stale-response guard (fetchBlock). The Campaign response blocks are kept apart from
// the deep-dive ones: the same endpoints are called with a different perPage/sort and
// a per-network timeline, and the two tabs must not overwrite each other's data.
// `data: null` means "not fetched yet" — distinct from a fetched-but-empty `[]`, so
// the GOLD hooks can tell a loading section (skeleton) from an empty one (hidden).
export const PERFORMANCE_BLOCKS = {
  // Campaign response (AAPU-6814). OVERVIEW is `/performance`: `{ header, kpis }`.
  OVERVIEW: 'overview',
  RESPONSE_TIMELINE: 'responseTimeline',
  RESPONSE_CREATORS: 'responseCreators',
  RESPONSE_POSTS: 'responsePosts',
  // Platform deep dive (AAPU-7043), one per AAPU-6853 endpoint.
  NETWORKS: 'networks',
  TIMELINE: 'timeline',
  POSTS: 'posts',
  CREATORS: 'creators',
  FORMATS: 'formats',
  HASHTAGS: 'hashtags',
  // "View all" list pages.
  ALL_CREATORS: 'allCreators',
  ALL_CONTENT: 'allContent',
};

// `key` is the endpoint (path + query) `data` was fetched with — see setBlockLoading.
const emptyBlock = () => ({ data: null, meta: null, key: null, loading: false, error: null });

const initialState = {
  selectedNetwork: DEFAULT_NETWORK,
  // `?days=` (7/14/30), shared by both tabs' timelines.
  timelineRange: DEFAULT_TIMELINE_RANGE,
  // Platform deep dive's views chart: a picked `{ from, to }` (Y-m-d) window, or null
  // to use `timelineRange`. Mirrors `?from=&to=`.
  deepDiveCustomRange: null,

  blocks: Object.values(PERFORMANCE_BLOCKS).reduce((acc, block) => {
    acc[block] = emptyBlock();
    return acc;
  }, {}),
};

const CampaignPerformanceSlice = createSlice({
  name: 'CampaignPerformance',
  initialState,
  reducers: {
    setSelectedNetwork(state, action) {
      state.selectedNetwork = action.payload;
    },
    setTimelineRange(state, action) {
      state.timelineRange = action.payload;
    },
    setDeepDiveCustomRange(state, action) {
      state.deepDiveCustomRange = action.payload;
    },
    // Stale-while-revalidate: a refetch of the same request (e.g. returning to a tab,
    // which remounts it) keeps showing the cached data and refreshes it in the
    // background; only a new request (other network/range/page) shows the skeleton.
    setBlockLoading(state, action) {
      const { block, key } = action.payload;
      const current = state.blocks[block];
      current.loading = !(current.key === key && current.data !== null);
      current.error = null;
    },
    setBlockData(state, action) {
      const { block, key, data, meta = null } = action.payload;
      state.blocks[block].data = data;
      state.blocks[block].meta = meta;
      state.blocks[block].key = key;
      state.blocks[block].loading = false;
    },
    setBlockError(state, action) {
      const { block, error } = action.payload;
      state.blocks[block].error = error;
      state.blocks[block].loading = false;
    },
  },
});

export const {
  setSelectedNetwork,
  setTimelineRange,
  setDeepDiveCustomRange,
  setBlockLoading,
  setBlockData,
  setBlockError,
} = CampaignPerformanceSlice.actions;

/**
 * Maps a non-success performance-endpoint response to a display-safe error string,
 * per the AAPU-6574 contract: a 400's `message` is safe to show as-is, a 422's
 * validation message is not (show the generic copy instead).
 * @param {Object} resp - the failed API response.
 * @returns {string}
 */
const toErrorMessage = (resp) => {
  if (resp?.status === 400 && resp?.message) return resp.message;
  return GENERIC_ERROR_MESSAGE;
};

/**
 * `/campaigns/{campaignId}/performance{path}` with `params` as the query string —
 * null/undefined/empty params are left out, so optional filters can be passed as-is.
 * The "All" network is left out too: the backend reads no `network` as every network.
 * @param {string|number} campaignId
 * @param {string} path - e.g. `'/timeline'`, or `''` for the overview.
 * @param {Object} [params]
 * @returns {string}
 */
const performanceEndpoint = (campaignId, path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key === 'network' && value === ALL_NETWORKS_KEY) return;
    if (value !== null && value !== undefined && value !== '') query.set(key, value);
  });
  const qs = query.toString();
  return `/campaigns/${campaignId}/performance${path}${qs ? `?${qs}` : ''}`;
};

// A picked `from`/`to` window wins over `days`, same as the backend.
const rangeParams = ({ days, from, to }) => (from && to ? { from, to } : { days });

// Latest request id per block — see fetchBlock.
const latestRequestIds = {};

/**
 * Shared fetch for every block. Switching platform (or a list page's filter or page)
 * can fire a block again before the previous response lands, so only the block's
 * latest request is applied — an older response is dropped (the newer request owns
 * the block's loading flag). Repeating the request the block already holds data for
 * refreshes it without a loading state (see setBlockLoading).
 * @param {Object} opts
 * @param {string} opts.block - a PERFORMANCE_BLOCKS value.
 * @param {string} opts.endpoint
 * @param {(resp: Object) => any} opts.toData - picks the block's data off the response.
 * @param {boolean} [opts.paged] - also store `resp.meta`.
 */
const fetchBlock = ({ block, endpoint, toData, paged = false }) => (dispatch) => {
  latestRequestIds[block] = (latestRequestIds[block] ?? 0) + 1;
  const requestId = latestRequestIds[block];
  dispatch(setBlockLoading({ block, key: endpoint }));

  arClient.doApiCallGet(endpoint, (resp) => {
    if (requestId !== latestRequestIds[block]) return;
    if (isApiSuccess(resp)) {
      dispatch(
        setBlockData({
          block,
          key: endpoint,
          data: toData(resp),
          meta: paged ? resp.meta ?? null : null,
        })
      );
    } else {
      dispatch(setBlockError({ block, error: toErrorMessage(resp) }));
    }
  });
};

const toArray = (resp) => (Array.isArray(resp.data) ? resp.data : []);
const toObject = (resp) => resp.data ?? null;

/** GET /performance/creators — paged, ranked creators. */
const fetchCreatorsBlock = (block, campaignId, params) =>
  fetchBlock({
    block,
    endpoint: performanceEndpoint(campaignId, '/creators', params),
    toData: toArray,
    paged: true,
  });

/** GET /performance/posts — paged, ranked posts. */
const fetchPostsBlock = (block, campaignId, params) =>
  fetchBlock({
    block,
    endpoint: performanceEndpoint(campaignId, '/posts', params),
    toData: toArray,
    paged: true,
  });

// ─── Campaign response (AAPU-6814, BE contract AAPU-6574) ───────────────────────

/**
 * GET /api/v1/campaigns/{campaignId}/performance — the header (also read by the
 * deep dive and list pages) and the KPI tiles.
 * @param {string|number} campaignId
 */
export const fetchPerformanceHeader = (campaignId) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.OVERVIEW,
    endpoint: performanceEndpoint(campaignId, ''),
    toData: (resp) => ({
      header: resp.data?.header ?? null,
      kpis: Array.isArray(resp.data?.kpis) ? resp.data.kpis : [],
    }),
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/timeline — campaign-wide daily EMV.
 * @param {string|number} campaignId
 * @param {{ days: 7|14|30 }} params
 */
export const fetchPerformanceTimeline = (campaignId, { days }) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.RESPONSE_TIMELINE,
    endpoint: performanceEndpoint(campaignId, '/timeline', { days }),
    toData: toObject,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/creators — "Top creators".
 * @param {string|number} campaignId
 * @param {{ network?: string }} params
 */
export const fetchPerformanceCreators = (campaignId, { network }) =>
  fetchCreatorsBlock(PERFORMANCE_BLOCKS.RESPONSE_CREATORS, campaignId, {
    perPage: LIST_PER_PAGE.TOP_CREATORS,
    network,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/posts — What's converting's "Top content".
 * @param {string|number} campaignId
 * @param {{ network?: string, sort?: 'views'|'engagements' }} params
 */
export const fetchPerformancePosts = (campaignId, { network, sort = 'views' }) =>
  fetchPostsBlock(PERFORMANCE_BLOCKS.RESPONSE_POSTS, campaignId, {
    sort,
    perPage: LIST_PER_PAGE.TOP_CONTENT,
    network,
  });

// ─── Platform deep dive (AAPU-7043, BE contract AAPU-6853) ──────────────────────

/**
 * GET /api/v1/campaigns/{campaignId}/performance/networks — one row per network the
 * campaign has deliverables on. No `network` filter; the FE picks the selected row.
 * @param {string|number} campaignId
 */
export const fetchPerformanceNetworks = (campaignId) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.NETWORKS,
    endpoint: performanceEndpoint(campaignId, '/networks'),
    toData: toArray,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/timeline?network= — per-network daily
 * series, including the TikTok One organic/sponsored view split.
 * @param {string|number} campaignId
 * @param {{ network: string, days: 7|14|30, from?: string, to?: string }} params
 */
export const fetchDeepDiveTimeline = (campaignId, { network, ...range }) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.TIMELINE,
    endpoint: performanceEndpoint(campaignId, '/timeline', { network, ...rangeParams(range) }),
    toData: toObject,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/posts?network= — "Best videos".
 * @param {string|number} campaignId
 * @param {{ network: string, sort: 'emv'|'views'|'engagements' }} params
 */
export const fetchDeepDivePosts = (campaignId, { network, sort }) =>
  fetchPostsBlock(PERFORMANCE_BLOCKS.POSTS, campaignId, {
    network,
    sort,
    perPage: LIST_PER_PAGE.BEST_VIDEOS,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/creators?network= — "Top creators".
 * @param {string|number} campaignId
 * @param {{ network: string }} params
 */
export const fetchDeepDiveCreators = (campaignId, { network }) =>
  fetchCreatorsBlock(PERFORMANCE_BLOCKS.CREATORS, campaignId, {
    network,
    sort: 'emv',
    perPage: LIST_PER_PAGE.DEEP_DIVE_TOP_CREATORS,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/formats?network= — EMV by content
 * format, highest first.
 * @param {string|number} campaignId
 * @param {{ network: string }} params
 */
export const fetchPerformanceFormats = (campaignId, { network }) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.FORMATS,
    endpoint: performanceEndpoint(campaignId, '/formats', { network }),
    toData: toArray,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/hashtags?network= — top hashtags
 * (ranked by `kpi`) and the week-over-week risers.
 * @param {string|number} campaignId
 * @param {{ network: string, kpi: 'views'|'engagements' }} params
 */
export const fetchPerformanceHashtags = (campaignId, { network, kpi }) =>
  fetchBlock({
    block: PERFORMANCE_BLOCKS.HASHTAGS,
    endpoint: performanceEndpoint(campaignId, '/hashtags', { network, kpi }),
    toData: toObject,
  });

// ─── "View all" list pages ──────────────────────────────────────────────────────

/**
 * GET /api/v1/campaigns/{campaignId}/performance/creators — the All creators page,
 * ranked by EMV. `network`/`creatorSize` are optional filters.
 * @param {string|number} campaignId
 * @param {{ page: number, perPage: number, network?: string|null, creatorSize?: string|null }} params
 */
export const fetchAllCreators = (campaignId, { page, perPage, network, creatorSize }) =>
  fetchCreatorsBlock(PERFORMANCE_BLOCKS.ALL_CREATORS, campaignId, {
    sort: 'emv',
    page,
    perPage,
    network,
    creatorSize,
  });

/**
 * GET /api/v1/campaigns/{campaignId}/performance/posts — the All content page, ranked
 * by EMV. `network` is an optional filter.
 * @param {string|number} campaignId
 * @param {{ page: number, perPage: number, network?: string|null }} params
 */
export const fetchAllContent = (campaignId, { page, perPage, network }) =>
  fetchPostsBlock(PERFORMANCE_BLOCKS.ALL_CONTENT, campaignId, {
    sort: 'emv',
    page,
    perPage,
    network,
  });

export default CampaignPerformanceSlice.reducer;
